import { useInfiniteQuery } from '@tanstack/react-query'
import { Empty, Flex, Select, Spin } from 'antd'
import { SelectProps } from 'antd/lib'
import { useMemo, useState } from 'react'

import {
  useStocksServiceGetAvailableConsumablesEndpointKey,
  useStocksServiceGetAvailableListEndpointKey
} from 'src/api/queries'
import {
  GetConsumableListResponse,
  GetMedicineListResponse,
  StocksService
} from 'src/api/requests'
import { useDebounce } from 'src/hooks'
import { useLocales } from 'src/locales'

export const ProductsAvailableSelect = (props: SelectProps) => {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const { t } = useLocales()

  const {
    data: medicinesData,
    isFetching: isFetchingMedicines,
    fetchNextPage: fetchNextMedicinesPage,
    hasNextPage: hasNextMedicinesPage
  } = useInfiniteQuery({
    queryKey: [useStocksServiceGetAvailableListEndpointKey, debouncedSearch],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await StocksService.getAvailableListEndpoint(
        pageParam,
        50,
        undefined,
        debouncedSearch
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data?: GetMedicineListResponse[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const {
    data: consumablesData,
    fetchNextPage: fetchNextConsumablesPage,
    isFetching: isFetchingConsumables,
    hasNextPage: hasNextConsumablesPage
  } = useInfiniteQuery({
    queryKey: [
      useStocksServiceGetAvailableConsumablesEndpointKey,
      debouncedSearch
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await StocksService.getAvailableConsumablesEndpoint(
        pageParam,
        50,
        undefined,
        debouncedSearch
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data?: GetConsumableListResponse[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const options = useMemo(() => {
    const medicines = medicinesData?.pages.flatMap((page) => page.data) || []
    const consumables =
      consumablesData?.pages.flatMap((page) => page.data) || []

    const allItems = [
      ...medicines.map((item) => ({
        label: `${item?.internationalName?.name} ${item?.doses}`,
        value: item?.internationalName?.id,
        item
      })),
      ...consumables.map((item) => ({
        label: item?.internationalName?.name,
        value: item?.internationalName?.id,
        item
      }))
    ]

    const seen = new Set<string>()
    const uniqueByInternationalName = allItems?.filter(({ item }) => {
      const id = item?.internationalName?.id
      if (!id || seen.has(id)) return false
      seen.add(id)
      return true
    })

    return uniqueByInternationalName
  }, [medicinesData, consumablesData])

  const isFetching = isFetchingConsumables || isFetchingMedicines

  return (
    <Select
      popupMatchSelectWidth={true}
      filterOption={false}
      options={options}
      onSearch={(value) => setSearch(value)}
      placeholder={t('medicine_register.international_name.title')}
      onClear={() => setSearch('')}
      loading={isFetching}
      showSearch={true}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
          hasNextMedicinesPage && fetchNextMedicinesPage()
          hasNextConsumablesPage && fetchNextConsumablesPage()
        }
      }}
      notFoundContent={
        isFetching ? (
          <Flex justify="center">
            <Spin spinning />
          </Flex>
        ) : (
          <Empty />
        )
      }
      {...props}
    />
  )
}
